import { createSelector } from 'reselect';
import { TIME_SERIES_CATEGORY } from '../../../CommonCharts/Trend/constants';
import { energyViewModes, STACKED_AREA_CHART } from './constants';

// eslint-disable-next-line import/no-unused-modules
export const getEnergyRelatedKPIsList = createSelector([
    ({ operationalParameters }) => operationalParameters?.availableKPIs?.trend?.data,
], (
    trendKPIs,
) => {
    const energyRelatedSignals = [];
    trendKPIs?.forEach((kpi) => {
        const kpiObj = kpi.data;
        const keys = Object.keys(kpiObj);
        keys?.forEach(key => {
            const newObj = kpiObj[key];
            // Filter for energy-related signals based on timeseriesCategory field from API
            const isEnergyByCategory = newObj.timeseriesCategory === TIME_SERIES_CATEGORY.ENERGY;
            if (isEnergyByCategory) {
                energyRelatedSignals.push(newObj);
            }
        });
    });
    return energyRelatedSignals;
});

export const getSelectedTrendKPIs = ({ operationalParameters }) => {
    return operationalParameters?.preselectedKPIs?.single?.trend;
};

export const getSelectedTrendsData = ({ operationalParameters }) => {
    return operationalParameters?.charts?.single?.trend[0]?.kpiList;
};

export const getSelectedAsset = ({ powertrainAssetDetails }) => {
    return powertrainAssetDetails?.selectedAsset;
};

export const getDateSelection = ({ overview }) => {
    return overview?.dateSelection;
};

export const getEnergyViewMode = ({ operationalParameters }) => {
    return operationalParameters?.energyViewMode || energyViewModes.OVERALL;
};

// Check if only energy-related signals are selected
export const getIsOnlyEnergySignalsSelected = createSelector([
    getSelectedTrendKPIs,
    getEnergyRelatedKPIsList,
    getSelectedTrendsData
], (selectedKPIs, energyKPIs, selectedTrendsData) => {
    if (!selectedKPIs?.length) {
        return false;
    }

    // Check if any selected signal has StackedAreaChart visualization
    const hasStackedAreaChart = selectedTrendsData?.some(
        item => item?.config?.details?.visualization === STACKED_AREA_CHART
    );

    if (hasStackedAreaChart) {
        return true;
    }

    const energyKPIKeys = new Set(energyKPIs.map(kpi => kpi.timeseriesKey));
    return selectedKPIs.every(selectedKPI => energyKPIKeys.has(selectedKPI));
});

// Check if any energy-related signals are selected along with operational parameters
export const getHasMixedSignalSelection = createSelector([
    getSelectedTrendKPIs,
    getEnergyRelatedKPIsList
], (selectedKPIs, energyKPIs) => {
    if (!selectedKPIs?.length) {
        return false;
    }

    const energyKPIKeys = new Set(energyKPIs.map(kpi => kpi.timeseriesKey));
    const hasEnergySignals = selectedKPIs.some(selectedKPI => energyKPIKeys.has(selectedKPI));
    const hasNonEnergySignals = selectedKPIs.some(selectedKPI => !energyKPIKeys.has(selectedKPI));

    return hasEnergySignals && hasNonEnergySignals;
});

// Get count of selected energy signals
export const getSelectedEnergySignalsCount = createSelector([
    getSelectedTrendKPIs,
    getEnergyRelatedKPIsList
], (selectedKPIs, energyKPIs) => {
    if (!selectedKPIs?.length) {
        return 0;
    }

    const energyKPIKeys = new Set(energyKPIs.map(kpi => kpi.timeseriesKey));
    return selectedKPIs.filter(selectedKPI => energyKPIKeys.has(selectedKPI)).length;
});

export const getModel = createSelector([
    getEnergyRelatedKPIsList,
    getSelectedTrendsData,
    getSelectedTrendKPIs,
    getSelectedAsset,
    getDateSelection,
    getEnergyViewMode,
    getIsOnlyEnergySignalsSelected,
    getHasMixedSignalSelection,
    getSelectedEnergySignalsCount
], (
    energyRelatedKPIsList,
    selectedTrendsData,
    selectedTrendKPIs,
    selectedAsset,
    dateSelection,
    energyViewMode,
    isOnlyEnergySignalsSelected,
    hasMixedSignalSelection,
    selectedEnergySignalsCount
) => {
    return {
        energyRelatedKPIsList,
        selectedTrendsData,
        selectedTrendKPIs,
        selectedAsset,
        dateSelection,
        energyViewMode,
        isOnlyEnergySignalsSelected,
        hasMixedSignalSelection,
        selectedEnergySignalsCount
    };
});
